import { useLegacyFunction } from '@common/context/legacy-function-provider';
import { useSpaceDomain } from '@common/hooks/use-space-domain';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { SpaceService } from '../../services/SpaceService';
import { UserService } from '../../services/UserService';
import { IResponse, ISpace, ISurvey } from '../../types';
import StyledButton from '../Controls/StyledButton';
import './style.scss';

interface IEmailVerifyAlert {
  survey: ISurvey | null;
  setIsSentryEnabled: (sentryState: boolean) => void;
}

const EmailVerificationAlert: FC<IEmailVerifyAlert> = ({ survey, setIsSentryEnabled }) => {
  const spaceDomain = useSpaceDomain();
  const { t } = useTranslation();
  const [space, setSpace] = useState<ISpace | undefined>(undefined);
  const [spaceIsLoaded, setSpaceIsLoaded] = useState<boolean>(false);
  const [isHideEmailResendLink, setIsHideEmailResendLink] = useState<boolean>(false);
  const { handleRequest, addToast } = useLegacyFunction();
  const { pathname } = useLocation();

  useEffect(() => {
    loadSpace();
  }, []);

  useEffect(() => {
    if (isHideEmailResendLink) {
      setIsHideEmailResendLink(false);
    }
  }, [pathname]);

  const loadSpace = () => {
    return new Promise<void>(resolve => {
      SpaceService.index({
        filter: { custom_domain: spaceDomain },
        include: 'portalSurveys,surveys',
        perPage: 1,
      })
        .then(({ body }) => {
          if (body.data.length) {
            setSpace(body.data[0]);
            setIsSentryEnabled(body.data[0]['module_sentry_is_activated']);
          }
        })
        .finally(() => {
          setSpaceIsLoaded(true);
          resolve();
        });
    });
  };

  if (!spaceIsLoaded) return null;

  const resendEmail = () => {
    return handleRequest(
      UserService.resendEmail({ survey_id: survey?.id, space_id: space?.id })
    )
      .then(() => {
        addToast(
          t<string>('pages.e-mail-verification.mail-resend-success'),
          'success',
        );
      })
      .catch((e: IResponse) => {
        if (e.statusCode !== 429)
          addToast(
            t<string>('common.error-appeared'),
            'error',
          );
      })
      .finally(() => {
        setIsHideEmailResendLink(true);
      });
  };

  return (
    <div className="email-verification-alert">
      <Container>
        <div className="email-verification-alert__wrapper">
          <FontAwesomeIcon icon={['fas', 'exclamation-circle']} />
          <div className="email-verification-alert__content">
            <span>
              {t<string>('pages.e-mail-verification.mail-not-verified-warning.text')}{' '}
            </span>
            {!isHideEmailResendLink && (
              <StyledButton className={'inline'} onClick={resendEmail}>
                {t<string>(
                  'pages.e-mail-verification.mail-not-verified-warning.confirm-e-mail'
                )}
              </StyledButton>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default EmailVerificationAlert;